import React, { useEffect, useState } from "react";
import { unitDisplay } from "../lib/constants";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Store, MapPin, Package, Tag, Ruler, Globe, Palette, Factory, ShoppingCart, ZoomIn, Plus, Phone, MessageCircle } from "lucide-react";
import PublicShell from "../components/PublicShell";
import ProductLightbox from "../components/store/ProductLightbox";
import CartDrawer from "../components/store/CartDrawer";
import CheckoutModal from "../components/store/CheckoutModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const nameOf = (p) => p.display_name || [p.category, p.type].filter(Boolean).join(" · ");

const ProductCard = ({ p, merchantId, onZoom, onAdd, inCart }) => {
  const src = `${API}/public/stores/${merchantId}/products/${p.id}/image`;
  return (
    <article className="bg-white rounded-2xl border border-[#7AB2B2]/60 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow] duration-200 flex flex-col" data-testid={`store-product-${p.id}`}>
      <button type="button" onClick={() => onZoom({ src, alt: nameOf(p) })} className="relative aspect-square bg-[#EBF4F6] w-full cursor-zoom-in group" data-testid={`store-product-image-btn-${p.id}`} aria-label="تكبير الصورة">
        <img src={src} alt={nameOf(p)} loading="lazy" className="w-full h-full object-cover" data-testid={`store-product-image-${p.id}`} />
        <span className={`absolute top-2 right-2 text-[11px] font-extrabold px-2.5 py-1 rounded-full ${p.is_available ? "bg-green-600 text-white" : "bg-gray-700/80 text-white"}`} data-testid={`store-product-availability-${p.id}`}>{p.availability}</span>
        <span className="absolute bottom-2 left-2 w-8 h-8 rounded-full bg-white/90 text-[#09637E] flex items-center justify-center shadow opacity-80 group-hover:opacity-100"><ZoomIn size={16} /></span>
      </button>
      <div className="p-3 space-y-2 flex-1 flex flex-col">
        <div className="font-extrabold text-[#333333] text-sm leading-snug line-clamp-2" data-testid={`store-product-name-${p.id}`}>{[p.category, p.type].filter(Boolean).join(" · ")}</div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[#09637E] font-extrabold text-lg" dir="ltr" data-testid={`store-product-price-${p.id}`}>${p.price_usd.toFixed(2)}</span>
          <span className="text-[11px] text-[#666666]" data-testid={`store-product-unit-${p.id}`}>{unitDisplay(p.unit).per}</span>
        </div>
        <ul className="text-xs text-[#666666] space-y-1 flex-1">
          {p.size && <li className="flex items-center gap-1"><Ruler size={12} /> المقاس: <span dir="ltr">{p.size}</span></li>}
          {p.country && <li className="flex items-center gap-1"><Globe size={12} /> المنشأ: {p.country}</li>}
          {p.color && <li className="flex items-center gap-1"><Palette size={12} /> اللون: {p.color}</li>}
          {p.manufacturer && <li className="flex items-center gap-1"><Factory size={12} /> الشركة: {p.manufacturer}</li>}
          {p.product_code && <li className="flex items-center gap-1"><Tag size={12} /> الكود: <span dir="ltr" className="font-mono">{p.product_code}</span></li>}
        </ul>
        <button type="button" disabled={!p.is_available} onClick={() => onAdd(p, src)} data-testid={`store-add-to-cart-${p.id}`}
          className={`h-10 rounded-lg font-bold text-sm flex items-center justify-center gap-1 disabled:opacity-50 ${inCart ? "bg-[#EBF4F6] text-[#09637E] border border-[#09637E]" : "bg-[#09637E] hover:bg-[#088395] text-white"}`}>
          <Plus size={16} /> {inCart ? "في السلة — أضف المزيد" : "أضف إلى السلة"}
        </button>
      </div>
    </article>
  );
};

export default function PublicStore() {
  const { merchantId } = useParams();
  const [store, setStore] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [state, setState] = useState("loading");
  const [zoom, setZoom] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkout, setCheckout] = useState(false);
  const LIMIT = 24;

  useEffect(() => { document.title = store ? `${store.store_name} — إمداد` : "إمداد"; }, [store]);
  useEffect(() => {
    setState("loading"); setCart([]);
    axios.get(`${API}/public/stores/${merchantId}`).then((r) => { setStore(r.data); setState("ok"); }).catch(() => setState("notfound"));
  }, [merchantId]);
  useEffect(() => {
    if (state !== "ok") return;
    axios.get(`${API}/public/stores/${merchantId}/products`, { params: { page, limit: LIMIT } })
      .then((r) => { setItems((prev) => (page === 1 ? r.data.items : [...prev, ...r.data.items])); setTotal(r.data.total); }).catch(() => {});
  }, [state, merchantId, page]);

  const addToCart = (p, image) => {
    setCart((c) => (c.find((l) => l.id === p.id) ? c.map((l) => (l.id === p.id ? { ...l, quantity: l.quantity + 1 } : l)) : [...c, { id: p.id, name: nameOf(p), price_usd: p.price_usd, quantity: 1, image, unit: p.unit }]));
    setCartOpen(true);
  };
  const setQty = (id, q) => setCart((c) => c.map((l) => (l.id === id ? { ...l, quantity: q } : l)));
  const remove = (id) => setCart((c) => c.filter((l) => l.id !== id));
  const count = cart.reduce((a, l) => a + l.quantity, 0);

  return (
    <PublicShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10" data-testid="public-store-page">
        {state === "loading" && <div className="text-center text-[#666666] py-20 font-bold">جارٍ التحميل...</div>}
        {state === "notfound" && (
          <div className="bg-white border border-[#7AB2B2] rounded-2xl p-12 text-center" data-testid="public-store-notfound">
            <Store className="mx-auto text-[#7AB2B2] mb-3" size={48} />
            <p className="font-extrabold text-[#333333] text-lg">هذا المتجر غير متاح</p>
            <Link to="/" className="inline-block mt-4 text-[#09637E] font-bold underline">العودة إلى إمداد</Link>
          </div>
        )}
        {state === "ok" && store && (
          <>
            <section className="relative overflow-hidden rounded-3xl bg-[#09637E] text-white p-6 md:p-10 mb-6 md:mb-8" data-testid="public-store-header">
              <div className="absolute -top-16 -left-16 w-56 h-56 rounded-full bg-white/10" />
              <div className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full bg-white/5" />
              <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-xs font-bold" data-testid="public-store-imdad-badge">
                    <span className="w-5 h-5 rounded bg-white text-[#09637E] flex items-center justify-center font-extrabold text-[11px]">إ</span> متجر على منصة إمداد
                  </div>
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight" data-testid="public-store-name">{store.store_name}</h1>
                  <div className="flex flex-wrap items-center gap-3 text-sm opacity-90">
                    {store.city && <span className="flex items-center gap-1" data-testid="public-store-city"><MapPin size={16} /> {store.city}</span>}
                    <span className="flex items-center gap-1" data-testid="public-store-count"><Package size={16} /> {store.product_count} منتج</span>
                    {store.phone_display && <span className="flex items-center gap-1" data-testid="public-store-phone"><Phone size={16} /> الهاتف: <span dir="ltr" className="font-bold">{store.phone_display}</span></span>}
                  </div>
                  {store.material_types?.length > 0 && <div className="flex flex-wrap gap-1.5 pt-1">{store.material_types.map((m) => <span key={m} className="text-xs bg-white/15 rounded-full px-2.5 py-0.5">{m}</span>)}</div>}
                </div>
                <div className="flex flex-wrap items-center gap-2 self-start md:self-auto">
                  {store.whatsapp && (
                    <a href={`https://wa.me/${store.whatsapp}`} target="_blank" rel="noreferrer" className="h-11 px-5 rounded-lg bg-[#25D366] text-white font-extrabold text-sm flex items-center gap-2 hover:bg-[#1ebe5d]" data-testid="public-store-whatsapp-btn" aria-label="تواصل معنا عبر واتساب">
                      <MessageCircle size={18} /> تواصل معنا
                    </a>
                  )}
                  <Link to="/register" className="h-11 px-5 rounded-lg bg-white text-[#09637E] font-extrabold text-sm flex items-center gap-2 hover:bg-[#EBF4F6]" data-testid="public-store-join">انضم إلى إمداد كتاجر</Link>
                </div>
              </div>
            </section>

            {items.length === 0 ? (
              <div className="bg-white border border-[#7AB2B2] rounded-2xl p-12 text-center text-[#666666] font-bold" data-testid="public-store-empty">لا توجد منتجات معروضة حالياً</div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5" data-testid="public-store-products">
                  {items.map((p) => <ProductCard key={p.id} p={p} merchantId={merchantId} onZoom={setZoom} onAdd={addToCart} inCart={!!cart.find((l) => l.id === p.id)} />)}
                </div>
                {items.length < total && (
                  <div className="text-center mt-6">
                    <button onClick={() => setPage((n) => n + 1)} data-testid="public-store-load-more" className="h-11 px-6 rounded-lg border-2 border-[#09637E] text-[#09637E] font-bold hover:bg-white">عرض المزيد ({total - items.length})</button>
                  </div>
                )}
              </>
            )}
            <p className="text-center text-xs text-[#666666] mt-8 pb-16" data-testid="public-store-footnote">الأسعار بالدولار الأمريكي {items.some((p) => p.unit && p.unit !== "meter") ? "حسب وحدة كل منتج" : "للمتر"} وتُحدَّث مباشرة من التاجر · الطلب عبر منصة إمداد والدفع عند الاستلام أو التسليم</p>

            {cart.length > 0 && (
              <button type="button" onClick={() => setCartOpen(true)} data-testid="cart-fab"
                className="fixed bottom-5 left-5 z-[90] h-14 pl-5 pr-4 rounded-full bg-[#09637E] hover:bg-[#088395] text-white font-extrabold shadow-xl flex items-center gap-2">
                <ShoppingCart size={20} /> السلة <span className="bg-white text-[#09637E] rounded-full min-w-[26px] h-[26px] px-1.5 flex items-center justify-center text-sm" data-testid="cart-fab-count" dir="ltr">{count}</span>
              </button>
            )}
          </>
        )}
      </div>
      {zoom && <ProductLightbox src={zoom.src} alt={zoom.alt} onClose={() => setZoom(null)} />}
      {cartOpen && <CartDrawer cart={cart} onQty={setQty} onRemove={remove} onClose={() => setCartOpen(false)} onCheckout={() => { setCartOpen(false); setCheckout(true); }} />}
      {checkout && <CheckoutModal merchantId={merchantId} cart={cart} onClose={() => setCheckout(false)} onPlaced={() => setCart([])} />}
    </PublicShell>
  );
}
